import React, { createContext, useState, useContext, ReactNode } from "react";
import { LoginData } from "api/shared/use-login";
import dayjs, { Dayjs } from "dayjs";

export enum USER_ROLE {
  ROLE_DOCTOR = "3",
  ROLE_PATIENT = "4",
}

type AuthContextType = {
  isAuthenticated: boolean;
  getRole: () => USER_ROLE | null;
  getHasAdminRights: () => boolean;
  getShowReserveListOnCalendar: () => boolean;
  getAppointmentTypeManagementIsAllowed: () => boolean;
  getShowPartnerSection: () => boolean;
  getShowProductsSection: () => boolean;
  getShowAddressFieldsOnNewClientForm: () => boolean;
  getShowAppointmentSchemaSection: () => boolean;
  getShowPatientSchemaSection: () => boolean;
  getIsClientMobileAppUsed: () => boolean;
  getShowPatientOrigin: () => boolean;
  getIsDemo: () => boolean;
  getDemoText: () => string;
  getIsZenPilates: () => boolean;
  getIsBooksyModule: () => boolean;
  getIsChatTurnedOff: () => boolean;
  getIsTestUser: () => boolean;
  getIsVerified: () => boolean;
  setVerifiedContext: (isVerified: boolean) => void;
  getIsAllowedAppointmentWithoutClientOrService: () => boolean;
  getShowCommissionStats: () => boolean;
  getEventsMinDate: () => Dayjs | undefined;
  loginContext: (data: LoginData) => void;
  logoutContext: () => void;
};

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    !!localStorage.getItem("authToken")
  );
  const [isVerified, setIsVerified] = useState<boolean>(
    localStorage.getItem("isVerified") === "true"
  );

  const loginContext = (data: LoginData): void => {
    localStorage.setItem("authToken", data.token);
    localStorage.setItem("role", data.role);
    localStorage.setItem("hasAdminRights", String(data.hasAdminRights));
    localStorage.setItem("showReserveListOnCalendar", String(data.showReserveListOnCalendar));
    localStorage.setItem("appointmentTypeManagementIsAllowed", String(data.appointmentTypeManagementIsAllowed));
    localStorage.setItem("showPartnerSection", String(data.showPartnerSection));
    localStorage.setItem("infoDemoText", String(data.infoDemo.message));
    localStorage.setItem("isDemo", String(data.infoDemo.visible));
    localStorage.setItem("showProductsSection", String(data.showProductsSection));
    localStorage.setItem("showAddressFieldsOnNewClientForm", String(data.showAddressFieldsOnNewClientForm));
    localStorage.setItem("showAppointmentSchemaSection", String(data.showAppointmentSchemaSection));
    localStorage.setItem("showPatientSchemaSection", String(data.showPatientSchemaSection));
    localStorage.setItem("showPatientOrigin", String(data.showPatientOrigin));
    localStorage.setItem("isClientMobileAppUsed", String(data.isClientMobileAppUsed));
    localStorage.setItem("isChatTurnedOff", String(data.isChatTurnedOff));
    localStorage.setItem("isZenPilates", String(data.isZenPilates));
    localStorage.setItem("booksyModule", String(data.booksyModule));
    localStorage.setItem("isTestUser", String(data.isTestUser ?? false));
    localStorage.setItem("isVerified", String(data.isVerified));
    localStorage.setItem("isAllowedAppointmentWithoutClientOrService", String(data.isAllowedAppointmentWithoutClientOrService));
    localStorage.setItem("showCommissionStats", String(data.showCommissionStats ?? false));
    localStorage.setItem("eventsDaysBackLimit", String(data.eventsDaysBackLimit ?? ""));
    setIsVerified(data.isVerified ?? false);
    setIsAuthenticated(true);
  };

  const logoutContext = (): void => {
    ["authToken", "role", "showPartnerSection", "infoDemoText", "isDemo", "hasAdminRights",
        "calendarDoctorId", "calendarDoctorName", "showReserveListOnCalendar", "appointmentTypeManagementIsAllowed",
        "selectedDoctorId", "selectedDoctorName", "showProductsSection", "showAddressFieldsOnNewClientForm",
        "showAppointmentSchemaSection", "showPatientSchemaSection", "isClientMobileAppUsed", "showPatientOrigin",
        "isZenPilates", "booksyModule", "isChatTurnedOff", "isTestUser", "isVerified", "isAllowedAppointmentWithoutClientOrService",
        "showCommissionStats", "eventsDaysBackLimit"
    ].forEach((key) => localStorage.removeItem(key));
    setIsAuthenticated(false);
  };

  const getRole = (): USER_ROLE | null => {
    return localStorage.getItem("role") as USER_ROLE | null;
  };

  const getHasAdminRights = (): boolean => {
    return localStorage.getItem("hasAdminRights") === "true";
  };

  const getShowReserveListOnCalendar = (): boolean => {
    return localStorage.getItem("showReserveListOnCalendar") === "true";
  };

  const getAppointmentTypeManagementIsAllowed = (): boolean => {
      const item = localStorage.getItem("appointmentTypeManagementIsAllowed");
      if (item) return item === "true";
      return true; // żeby nie zwracało false jak uzytkownik się jeszcze nie przelogował po dodaniu nowego klucza
  };

  const getShowPartnerSection = (): boolean => {
    return localStorage.getItem("showPartnerSection") === "true";
  };

  const getShowProductsSection = (): boolean => {
    return localStorage.getItem("showProductsSection") === "true";
  };

  const getShowAddressFieldsOnNewClientForm = (): boolean => {
    return localStorage.getItem("showAddressFieldsOnNewClientForm") === "true";
  };

  const getShowAppointmentSchemaSection = (): boolean => {
    const item = localStorage.getItem("showAppointmentSchemaSection");
    if (item) return item === "true";
    return true;
  };

  const getShowPatientSchemaSection = (): boolean => {
    return localStorage.getItem("showPatientSchemaSection") === "true";
  };

  const getIsClientMobileAppUsed = (): boolean => {
    const item = localStorage.getItem("isClientMobileAppUsed");
    if (item) return item === "true";
    return true;
  };

  const getIsChatTurnedOff = (): boolean => {
    return localStorage.getItem("isChatTurnedOff") === "true";
  };

  const getIsDemo = (): boolean => {
    return localStorage.getItem("isDemo") === "true";
  };

  const getDemoText = (): string => {
    return localStorage.getItem("infoDemoText");
  };
  const getShowPatientOrigin = (): boolean => {
    return localStorage.getItem("showPatientOrigin") === "true";
  };
  const getIsZenPilates = (): boolean => {
    return localStorage.getItem("isZenPilates") === "true";
  };
  const getIsBooksyModule = (): boolean => {
    return localStorage.getItem("booksyModule") === "true";
  };

  const getIsTestUser = (): boolean => {
    return localStorage.getItem("isTestUser") === "true";
  };
  const getIsVerified = (): boolean => isVerified;

  const setVerifiedContext = (value: boolean): void => {
    localStorage.setItem("isVerified", String(value));
    setIsVerified(value);
  };
  const getIsAllowedAppointmentWithoutClientOrService = (): boolean => {
    return localStorage.getItem("isAllowedAppointmentWithoutClientOrService") === "true";
  };

  const getShowCommissionStats = (): boolean => {
    return localStorage.getItem("showCommissionStats") === "true";
  };

  // Parametr "Terminarz - Limit dni wstecz dla wydarzeń" - undefined = brak ograniczenia (administrator
  // lub użytkownik nieprzelogowany po dodaniu klucza; serwer i tak waliduje datę)
  const getEventsMinDate = (): Dayjs | undefined => {
    const item = localStorage.getItem("eventsDaysBackLimit");
    if (!item) return undefined;
    return dayjs().startOf("day").subtract(Number(item), "day");
  };

  const value: AuthContextType = {
    isAuthenticated,
    getRole,
    getIsDemo,
    getDemoText,
    getShowPartnerSection,
    getShowReserveListOnCalendar,
    getAppointmentTypeManagementIsAllowed,
    getShowAddressFieldsOnNewClientForm,
    loginContext,
    logoutContext,
    getHasAdminRights,
    getShowProductsSection,
    getShowAppointmentSchemaSection,
    getShowPatientSchemaSection,
    getIsClientMobileAppUsed,
    getShowPatientOrigin,
    getIsZenPilates,
    getIsBooksyModule,
    getIsChatTurnedOff,
    getIsTestUser,
    getIsVerified,
    getEventsMinDate,
    setVerifiedContext,
    getIsAllowedAppointmentWithoutClientOrService,
    getShowCommissionStats,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
